// ============================================================
// DM-LMS — useConfig Hook
// DRY: one hook, one cache, one fetch pattern for every admin-managed
// dropdown (profession, county, payment method, ...) — GET /api/config.
// ============================================================

import { useCallback, useEffect, useState } from "react";
import type { ConfigCategory, ConfigOption } from "@dm-lms/shared/types";
import { getJson } from "./useApi.js";

const CACHE_TTL_MS = 5 * 60 * 1000;
const LOAD_ERROR = "Failed to load options";

// Module-level (not React state) so the cache outlives any single component.
const cache = new Map<ConfigCategory, { data: ConfigOption[]; fetchedAt: number }>();
// One network request per category at a time — concurrent mounts share it.
const inFlight = new Map<ConfigCategory, Promise<ConfigOption[]>>();

function readCache(category: ConfigCategory): ConfigOption[] | undefined {
  const cached = cache.get(category);
  if (!cached) return undefined;
  if (Date.now() - cached.fetchedAt >= CACHE_TTL_MS) {
    cache.delete(category);
    return undefined;
  }
  return cached.data;
}

function fetchOptions(category: ConfigCategory): Promise<ConfigOption[]> {
  const shared = inFlight.get(category);
  if (shared) return shared;

  const request: Promise<ConfigOption[]> = getJson<ConfigOption[]>(`/api/config?category=${category}`)
    .catch(() => {
      // Network / non-JSON failures: show a friendly message, not the raw fetch error.
      throw new Error(LOAD_ERROR);
    })
    .then((res) => {
      if (!res.ok) throw new Error(res.error || LOAD_ERROR);
      // invalidate() during the request drops it from inFlight — don't cache a result it meant to discard.
      if (inFlight.get(category) === request) {
        cache.set(category, { data: res.data, fetchedAt: Date.now() });
      }
      return res.data;
    })
    .finally(() => {
      if (inFlight.get(category) === request) inFlight.delete(category);
    });

  inFlight.set(category, request);
  return request;
}

// Standalone so non-consumers (e.g. the admin config page) can clear a category without mounting the hook.
export function invalidateConfig(category: ConfigCategory): void {
  cache.delete(category);
  inFlight.delete(category);
}

export function useConfig(category: ConfigCategory) {
  const [options, setOptions] = useState<ConfigOption[]>(() => readCache(category) ?? []);
  const [loading, setLoading] = useState(() => readCache(category) === undefined);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isCancelled = false;

    const cached = readCache(category);
    if (cached) {
      setOptions(cached);
      setError(null);
      setLoading(false);
      return;
    }

    setOptions([]);
    setError(null);
    setLoading(true);

    fetchOptions(category)
      .then((data) => {
        if (!isCancelled) setOptions(data);
      })
      .catch((err: unknown) => {
        if (!isCancelled) setError(err instanceof Error && err.message ? err.message : LOAD_ERROR);
      })
      .finally(() => {
        if (!isCancelled) setLoading(false);
      });

    return () => {
      isCancelled = true;
    };
  }, [category]);

  const invalidate = useCallback(() => {
    invalidateConfig(category);
  }, [category]);

  return { options, loading, error, invalidate };
}
